import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle } from "react-feather";
import {
  addCookingProgress,
  updateCookingProgress,
  selectActiveProgressByRecipeId,
  recordCook,
  setRecipeCooked,
  useAppDispatch,
  useAppSelector,
  formatMultiplier,
  useT,
  type RootState,
} from "@reciperoll/core";
import { ScaleControl } from "./ScaleControl";
import { useToast } from "./Toast";

// Scales are floats (servings/base can be e.g. 1.333…), so compare with a tolerance
// rather than === when deciding whether the picker still matches the started scale.
const EPS = 1e-3;

interface StartCookingProps {
  recipeId: string;
  // Base servings enables the servings slider (scaling by target servings);
  // null/absent falls back to the multiplier pills. `initialServings` carries the
  // scale set on the detail page so a fresh cook starts there, not at Original.
  baseServings?: number | null;
  initialServings?: number;
}

// "Start cooking" (or Continue) with a serving-scale picker. On start it creates a
// cookingProgress session carrying the chosen servingMultiplier, which cook mode
// reads to scale ingredients. Mirrors the RN Start Cooking bottom sheet.
export function StartCooking({
  recipeId,
  baseServings = null,
  initialServings,
}: StartCookingProps) {
  const t = useT();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const active = useAppSelector((s: RootState) =>
    selectActiveProgressByRecipeId(s, recipeId),
  );

  // Quick-log a cook without the step-by-step flow: records a cook + marks the
  // recipe cooked, with a toast. Low-stakes and reversible, so no confirm.
  const handleRecordCook = () => {
    dispatch(recordCook({ recipeId }));
    dispatch(setRecipeCooked({ id: recipeId, cooked: true }));
    toast.show(t("recipe.loggedCook"));
  };

  const hasServings = !!baseServings && baseServings > 0;
  const initialScale =
    hasServings && initialServings ? initialServings / baseServings! : 1;

  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  // Second-step confirmation for Start Fresh (it discards progress). Shown inline in
  // the modal rather than window.confirm so it matches the app's styling.
  const [confirmingFresh, setConfirmingFresh] = useState(false);

  const openModal = () => {
    // Resume an in-progress cook at the multiplier it was started with (retained);
    // otherwise seed from the scale carried in from the detail page.
    setScale(active ? active.servingMultiplier : initialScale);
    setConfirmingFresh(false);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setConfirmingFresh(false);
  };

  // The scale the in-progress cook was started with, and whether the picker has been
  // moved off it. A session underway can only be resumed at its original scale (the
  // steps done so far assumed it), so moving the picker disables Resume and makes
  // Start Fresh (which restarts at the new scale) the live action instead.
  const startedScale = active?.servingMultiplier ?? 1;
  const scaleChanged = !!active && Math.abs(scale - startedScale) > EPS;

  // A short descriptor of a scale: "serves 8" when the recipe has base servings,
  // otherwise "2×". Used in the Start Fresh label and its confirmation.
  const scaleLabel = (s: number) =>
    hasServings
      ? t("cook.scaleServes", { count: Math.round(s * baseServings!) })
      : t("cook.scaleMultiplier", { multiplier: formatMultiplier(s) });

  const cook = () => navigate(`/recipe/${recipeId}/cook`);

  const start = () => {
    dispatch(
      addCookingProgress({
        recipeId,
        currentStep: 1,
        status: "in_progress",
        servingMultiplier: scale,
      }),
    );
    cook();
  };

  const startFresh = () => {
    if (!active) return;
    dispatch(
      updateCookingProgress({
        ...active,
        currentStep: 1,
        status: "in_progress",
        servingMultiplier: scale,
      }),
    );
    cook();
  };

  return (
    <>
      <div className="flex gap-3 mb-3">
        {/* Start / Continue cooking — primary. Takes 2/3 on wide screens so the
            record button shares the row without leaving it over-wide. */}
        <button
          onClick={openModal}
          className="flex-1 bg-terracotta hover:bg-terracotta-dark transition-colors text-white rounded-2xl py-4 text-base text-center font-bold cursor-pointer"
        >
          {active
            ? active.servingMultiplier !== 1
              ? t("cook.continueCookingMultiplier", { multiplier: formatMultiplier(active.servingMultiplier) })
              : t("recipe.continueCooking")
            : t("recipe.startCooking")}
        </button>

        {/* Record a cook — quick-log without the step-by-step flow. A compact
            icon-only square beside the primary (matches the RN phone treatment),
            since the web cook bar sits in a narrow column. A high-contrast neutral
            (near-black light / warm dark-grey dark) so it reads as secondary
            without competing with the terracotta primary. */}
        <button
          onClick={handleRecordCook}
          aria-label={t("recipe.recordCook")}
          title={t("recipe.recordCook")}
          className="w-14 shrink-0 flex items-center justify-center rounded-2xl py-4 bg-ink dark:bg-surface-2 text-white hover:opacity-90 transition-opacity cursor-pointer"
        >
          <CheckCircle className="w-5 h-5" />
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={close}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-surface p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-ink mb-1">
              {active ? t("cook.resumeCooking") : t("cook.readyToCook")}
            </h2>
            <div className="mb-6">
              <ScaleControl
                baseServings={baseServings}
                scale={scale}
                onChange={setScale}
              />
            </div>

            {confirmingFresh ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-ink-soft mb-2">
                  {scaleChanged
                    ? t("cook.startFreshRescaleMessage", { scale: scaleLabel(scale) })
                    : t("cook.startFreshMessage")}
                </p>
                <button
                  onClick={startFresh}
                  className="w-full rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors py-3 font-semibold text-white"
                >
                  {t("cook.startFresh")}
                </button>
                <button
                  onClick={() => setConfirmingFresh(false)}
                  className="w-full rounded-xl py-2 text-sm font-semibold text-ink-faint hover:text-ink-soft"
                >
                  {t("common.cancel")}
                </button>
              </div>
            ) : active ? (
              <div className="flex flex-col gap-2">
                {/* Resume keeps the started scale; disabled once the picker is moved
                    off it (can't rescale a cook mid-way) — slide back to re-enable.
                    Label always shows the ORIGINAL scale, for contrast below. */}
                <button
                  onClick={cook}
                  disabled={scaleChanged}
                  className={`w-full rounded-xl py-3 font-semibold transition-colors ${
                    scaleChanged
                      ? "bg-surface-2 text-ink-faint opacity-50 cursor-not-allowed"
                      : "bg-terracotta hover:bg-terracotta-dark text-white"
                  }`}
                >
                  {active.servingMultiplier !== 1
                    ? t("cook.resumeMultiplier", { multiplier: formatMultiplier(active.servingMultiplier) })
                    : t("cook.resume")}
                </button>
                {/* Start Fresh becomes the primary once the picker is moved (it
                    restarts at the new scale); otherwise a quiet secondary. */}
                <button
                  onClick={() => setConfirmingFresh(true)}
                  className={`w-full rounded-xl py-3 font-semibold transition-colors ${
                    scaleChanged
                      ? "bg-terracotta hover:bg-terracotta-dark text-white"
                      : "border border-line bg-surface-2 hover:bg-line text-ink-soft"
                  }`}
                >
                  {scaleChanged
                    ? hasServings
                      ? t("cook.startFreshServes", { count: Math.round(scale * baseServings!) })
                      : t("cook.startFreshMultiplier", { multiplier: formatMultiplier(scale) })
                    : t("cook.startFresh")}
                </button>
                <button
                  onClick={close}
                  className="w-full rounded-xl py-2 text-sm font-semibold text-ink-faint hover:text-ink-soft"
                >
                  {t("common.cancel")}
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={close}
                  className="flex-1 rounded-xl border border-line py-3 font-semibold text-ink-soft hover:bg-surface-2"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={start}
                  className="flex-1 rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors py-3 font-semibold text-white"
                >
                  {t("recipe.startCooking")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
